import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../../../utils/cn";
import {
  AGENT_AVATAR_MAX,
  AGENT_DESCRIPTION_MAX,
  AGENT_NAME_MAX,
  AGENT_PRIORITY_MAX,
} from "../agentPayload";
import {
  CharCounter,
  ChipRadioGroup,
  CONTROL_CLS,
  SectionTitle,
  TEXTAREA_CLS,
} from "./primitives";

// Identidad del agente: cómo se llama, con qué cara aparece, si está
// encendido, a quién atiende y con qué prioridad compite contra otros agentes.
//
// `locked` (el agente está configurado por encima del alcance del llamador)
// apaga todo menos el ESTADO: es la única cosa que ese rol puede cambiar —para
// pausarlo— y por eso vive acá y no dentro de un fieldset deshabilitado.

const STATUS_OPTIONS = [
  { value: "active", label: "Activo" },
  { value: "paused", label: "Pausado" },
  { value: "draft", label: "Borrador" },
];

export default function IdentitySection({
  state,
  audiences = [],
  errors = {},
  nameRef,
  onChange,
  locked = false,
}) {
  const { t } = useTranslation();

  return (
    <section className="space-y-4">
      <SectionTitle>{t("Identidad")}</SectionTitle>

      {/* Nombre + descripción */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <label htmlFor="agent-name" className="hd-field-label block text-xs font-medium text-fg">
            {t("Nombre")}
          </label>
          <input
            id="agent-name"
            ref={nameRef}
            type="text"
            value={state.name}
            maxLength={AGENT_NAME_MAX}
            onChange={(e) => onChange("name", e.target.value)}
            disabled={locked}
            placeholder={t("Ej.: Atención comercios")}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "agent-name-error" : undefined}
            className={CONTROL_CLS}
          />
          {errors.name ? (
            <p id="agent-name-error" className="text-[12px] md:text-[11px] text-status-error" role="alert">
              {t(errors.name)}
            </p>
          ) : (
            <p className="text-[12px] md:text-[11px] text-fg-muted">
              {t("Solo lo ve tu equipo; el cliente nunca lee este nombre.")}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <label
            htmlFor="agent-avatar"
            className="hd-field-label block text-xs font-medium text-fg"
          >
            {t("Avatar")}
          </label>
          <div className="flex items-center gap-2">
            <span className="text-2xl leading-none select-none shrink-0" aria-hidden="true">
              {state.avatar || "🤖"}
            </span>
            <input
              id="agent-avatar"
              type="text"
              value={state.avatar}
              maxLength={AGENT_AVATAR_MAX}
              onChange={(e) => onChange("avatar", e.target.value)}
              disabled={locked}
              placeholder="🤖"
              aria-label={t("Avatar (emoji)")}
              className={cn(CONTROL_CLS, "!w-24 text-center")}
            />
            <p className="text-[12px] md:text-[11px] text-fg-muted">
              {t("Un emoji para reconocerlo en la lista.")}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-1">
        <label
          htmlFor="agent-description"
          className="hd-field-label block text-xs font-medium text-fg"
        >
          {t("Descripción")}
        </label>
        <textarea
          id="agent-description"
          rows={2}
          value={state.description}
          maxLength={AGENT_DESCRIPTION_MAX}
          onChange={(e) => onChange("description", e.target.value)}
          disabled={locked}
          placeholder={t("Ej.: Responde a los comercios que despachan con nosotros.")}
          className={TEXTAREA_CLS}
        />
        <div className="flex items-center justify-between gap-2">
          <p className="text-[12px] md:text-[11px] text-fg-muted">
            {t("Para qué sirve este agente, en una línea.")}
          </p>
          <CharCounter value={(state.description || "").length} max={AGENT_DESCRIPTION_MAX} />
        </div>
      </div>

      {/* Estado */}
      <div className="space-y-2">
        <span className="hd-field-label block text-xs font-medium text-fg">{t("Estado")}</span>
        <ChipRadioGroup
          value={state.status}
          options={STATUS_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
          onChange={(next) => onChange("status", next)}
          ariaLabel={t("Estado del agente")}
          describedBy="agent-status-help"
        />
        <p id="agent-status-help" className="text-[12px] md:text-[11px] text-fg-muted">
          {state.status === "active"
            ? t("Está respondiendo según su horario y sus canales.")
            : t("Solo los agentes activos responden mensajes. Puedes guardarlo así mientras lo preparas.")}
        </p>
      </div>

      {/* Audiencia */}
      <div className="space-y-2">
        <span className="hd-field-label block text-xs font-medium text-fg">{t("Audiencia")}</span>
        {audiences.length > 0 ? (
          <div role="radiogroup" aria-label={t("Audiencia del agente")} className="space-y-2">
            {audiences.map((a) => (
              <label key={a.id} className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="radio"
                  name="agent-audience"
                  value={a.id}
                  checked={state.audience === a.id}
                  onChange={() => onChange("audience", a.id)}
                  disabled={locked}
                  className="mt-0.5 !w-5 !h-5 md:!w-4 md:!h-4 accent-brand-500 shrink-0"
                />
                <span className="min-w-0">
                  <span className="block text-xs font-medium text-fg">{a.label}</span>
                  {a.description && (
                    <span className="block text-[12px] md:text-[11px] text-fg-muted">
                      {a.description}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-[12px] md:text-[11px] text-fg-muted">
            {t("No pudimos cargar las audiencias disponibles.")}
          </p>
        )}
        <p className="text-[12px] md:text-[11px] text-fg-muted">
          {t("Define con quién habla este agente y qué capacidades puede usar.")}
        </p>
      </div>

      {/* Prioridad */}
      <div className="space-y-1">
        <label
          htmlFor="agent-priority"
          className="hd-field-label block text-xs font-medium text-fg"
        >
          {t("Prioridad")}
        </label>
        <input
          id="agent-priority"
          type="number"
          inputMode="numeric"
          min={0}
          max={AGENT_PRIORITY_MAX}
          step={1}
          value={state.priority}
          onChange={(e) => onChange("priority", e.target.value)}
          disabled={locked}
          className={cn(CONTROL_CLS, "sm:!w-32")}
        />
        <p className="text-[12px] md:text-[11px] text-fg-muted">
          {t("De 0 a 100: mayor = se evalúa primero cuando varios agentes podrían responder.")}
        </p>
      </div>
    </section>
  );
}
